import {
  ClaimDTOStatusEnum,
  OrderCustomerDTO,
  OrderWithOrderCustomersDTOStatusIdEnum,
  TenancyOrderWithTenancyDTO,
  PaymentPlanDTOStatusEnum,
} from '@reposit/api-client';
import { getDashboardRedirect, getOutstandingRepositAction } from './getDashboardRedirect';
import { get } from 'lodash';
import React, { Fragment, useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-grid-system';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router';
import styled from 'styled-components';
import HammockImage from '../../assets/svg/hammock.svg';
import Megaphone from '../../assets/svg/megaphone.svg';
import { getBreakpoint } from '../../base/style';
import Filter from '../../components/Filter';
import { FullPageLoader } from '../../components/Loading/index';
import RepositList from '../../components/Reposit/RepositList';
import { Caption, Header2, Header4 } from '../../components/Typography';
import { RepositFilter } from '../../constants/reposit';
import { fetchCustomerRequested } from '../../redux/account/account.actions';
import { getCurrentCustomerId } from '../../redux/account/account.selectors';
import { getCustomerById } from '../../redux/entities/entities.selectors';
import { createLoadingSelector } from '../../redux/loading/loading.selector';
import {
  fetchTenancyAnniversariesRequested,
  fetchTenancyOrdersRequested,
  FETCH_TENANCY_ANNIVERSARIES_STORE_KEY,
  FETCH_TENANCY_ORDERS_STORE_KEY,
  FETCH_ORDER_CUSTOMER_STORE_KEY,
} from '../../redux/order/order.actions';
import {
  getFilteredTenancyOrders,
  getTenancyAnniversaries,
  getTenancyOrders,
} from '../../redux/order/order.selectors';
import { AppState } from '../../redux/root.reducer';
import { getPaymentPlanByClaimId } from '../../redux/payment-plan/payment-plan.selectors';
import { isTenancyOrderExpired } from '../../redux/utils/is-tenancy-order-expired';

interface DashboardProps {}

const ContentHeader = styled.div`
  text-align: center;
`;

const Subtitle = styled(Caption)`
  max-width: 540px;
  margin: 0 auto;
`;

const filters = [
  {
    value: RepositFilter.ACTIVE,
    label: 'Active',
  },
  {
    value: RepositFilter.EXPIRED,
    label: 'Expired',
  },
];

const Title = styled(Header2)`
  color: ${(props) => props.theme.colors.negative};
  margin: 24px 0 1rem;
`;

const RelaxImage = styled.div`
  background: transparent url(${HammockImage}) no-repeat center center;
  background-size: 50%;
  height: 300px;
  margin: -20px 0 -36px;
  width: 100%;

  @media screen and (max-width: ${getBreakpoint('md')}) {
    height: 510px;
    margin: -110px 0 -36px;
    display: none;
  }
`;

const MegaphoneImage = styled.img`
  @media screen and (max-width: ${getBreakpoint('md')}) {
    display: none;
  }
`;

const NoRepositsWrapper = styled.div`
  padding: 96px 0 0;
  text-align: center;
`;

const StyledContainer = styled(Container)`
  @media screen and (min-width: ${getBreakpoint('md')}) {
    padding-top: 36;
  }
`;

const NoReposits: React.FC<{ status: string }> = ({ status }) => {
  let text = '';

  if (status === 'ACTIVE') text = 'You dont have any active Reposits.';
  if (status === 'EXPIRED') text = 'You dont have any expired Reposits.';

  return (
    <NoRepositsWrapper>
      <Header4>{text}</Header4>
    </NoRepositsWrapper>
  );
};


const Dashboard: React.FC<DashboardProps> = (props) => {
  const dispatch = useDispatch();
  const [hasRequestedDashboardData, setHasRequestedDashboardData] = useState(false);
  const customerId = useSelector(getCurrentCustomerId);
  const customer = useSelector(getCustomerById(customerId));

  const loadingSelector = createLoadingSelector([
    FETCH_TENANCY_ORDERS_STORE_KEY,
    FETCH_TENANCY_ANNIVERSARIES_STORE_KEY,
    FETCH_ORDER_CUSTOMER_STORE_KEY,
  ]);
  const isLoading = useSelector(loadingSelector);
  const tenancyOrders = useSelector(getTenancyOrders);
  const tenancyAnniversaries = useSelector(getTenancyAnniversaries);
  const activeRepositAvailable = tenancyOrders.some((to) => {
    const isExpired = isTenancyOrderExpired(to);
    return !isExpired;
  });
  const { hasOutstandingRepositActions, repositActionOrderId } = getOutstandingRepositAction(tenancyOrders, customerId);
  const setFilterToActive = activeRepositAvailable || hasOutstandingRepositActions;

  const [filter, setFilter] = useState<string>(setFilterToActive ? RepositFilter.ACTIVE : RepositFilter.EXPIRED);
  useEffect(() => {
    if (setFilterToActive) {
      setFilter(RepositFilter.ACTIVE);
    } else {
      setFilter(RepositFilter.EXPIRED);
    }
  }, [setFilterToActive]);

  const filteredTenancyOrders = useSelector((state: AppState) => getFilteredTenancyOrders(state, filter));
  const allowedClaimStatuses = [
    ClaimDTOStatusEnum.AWAITINGRESPONDENTS,
    ClaimDTOStatusEnum.AWAITINGARBITRATION,
    ClaimDTOStatusEnum.AWAITINGARBITRATIONADMINFEE,
    ClaimDTOStatusEnum.ARBITRATED,
    ClaimDTOStatusEnum.MEDIATION,
    ClaimDTOStatusEnum.AWAITINGSUBSTANTIATION,
    ClaimDTOStatusEnum.AWAITINGEVIDENCEREVIEW,
    ClaimDTOStatusEnum.EVIDENCEREVIEW,
  ];
  const tenancyWithClaim: TenancyOrderWithTenancyDTO = tenancyOrders.find((tenancyOrder: TenancyOrderWithTenancyDTO): boolean => {
    const claim = get(tenancyOrder, 'tenancy.checkout.claim', null);
    return claim && allowedClaimStatuses.indexOf(claim.status) !== -1;
  });

  const activeUnpaidTopUp = tenancyAnniversaries.find((tenancyAnniversary) => {
    if (tenancyAnniversary.order) {
      const { order } = tenancyAnniversary;
      const isPending = order.statusId === OrderWithOrderCustomersDTOStatusIdEnum.PENDING;
      const orderCustomer = order.orderCustomers.find((oc: OrderCustomerDTO) => {
        return oc.customer.id === customerId;
      });
      const hasUnpaidAction =
        orderCustomer && orderCustomer.actions ? orderCustomer.actions.find((action) => !action.completedAt) : false;
      if (isPending && hasUnpaidAction) {
        return true;
      }
    }
    return false;
  });

  const claim = get(tenancyWithClaim, 'tenancy.checkout.claim', null);
  const claimId = get(claim, 'id', null);
  const arbitration = get(claim, 'arbitration', null);
  const claimAutoChargeEnabled = get(claim, 'autoChargeEnabled', null);
  const paymentPlan = useSelector(getPaymentPlanByClaimId(claimId, customerId));

  useEffect(() => {
    dispatch(fetchTenancyOrdersRequested());
    dispatch(fetchTenancyAnniversariesRequested());
    setHasRequestedDashboardData(true);
  }, [dispatch]);

  /**
   * Ensures the customer record is fetched when `customerId` is not yet
   * available in the Redux store. This can happen on a hard refresh or initial
   * load before the account slice has been populated. Without it, any redirect
   * that requires a `customerId` (e.g. checkout, annual-fee) would race
   * against an undefined value and either render a loader indefinitely or
   * navigate to a broken URL.
   */
  useEffect(() => {
    if (!customerId) {
      dispatch(fetchCustomerRequested());
    }
  }, [dispatch, customerId]);

  const hasLoadedDashboardData = hasRequestedDashboardData && !isLoading;
  const hasPaymentPlan =
    paymentPlan &&
    (paymentPlan.status === PaymentPlanDTOStatusEnum.ACTIVE || paymentPlan.status === PaymentPlanDTOStatusEnum.PENDING);

  const redirect = getDashboardRedirect({
    hasLoadedDashboardData,
    paymentPlan,
    hasPaymentPlan: !!hasPaymentPlan,
    claimId,
    claimAutoChargeEnabled,
    arbitration,
    hasOutstandingRepositActions,
    repositActionOrderId,
    customerId,
    activeUnpaidTopUp,
    customer,
  });

  if (redirect) {
    if ('waitForDashboardData' in redirect || 'waitForCustomer' in redirect) return <FullPageLoader />;
    return <Redirect to={redirect.to} />;
  }

  return (
    <StyledContainer>
      <Row>
        <Col sm={12}>
          {hasOutstandingRepositActions ? (
            <Fragment>
              <Row>
                <Col lg={4} style={{ textAlign: 'center' }}>
                  <MegaphoneImage src={Megaphone} width="200" alt="megaphone" />
                </Col>
                <Col lg={8}>
                  <Title>You have outstanding actions</Title>
                  <Caption>There are actions required on your Reposits. Please complete them.</Caption>
                </Col>
              </Row>
            </Fragment>
          ) : (
            <ContentHeader>
              <Header2>Sit back and relax</Header2>
              <Subtitle>There’s nothing for you to do right now. We will be in touch if anything changes</Subtitle>
              <RelaxImage />
            </ContentHeader>
          )}
        </Col>
      </Row>
      <Row>
        <Col sm={12}>
          <div style={{ marginTop: 36 }}>
            <Filter onSelect={(filter) => setFilter(filter)} options={filters} label="Filter by status" selected={filter} />
          </div>
        </Col>
      </Row>
      <Row>
        <Col sm={12}>
          {filteredTenancyOrders.length ? <RepositList reposits={filteredTenancyOrders} /> : <NoReposits status={filter} />}
        </Col>
      </Row>
    </StyledContainer>
  );
};

export default Dashboard;
